import { Box, Typography } from "@mui/material";
import { useEndorsements } from "../hooks/useEndorsements";
import { EndorsementConfigForm } from "./EndorsementConfigForm";

interface EndorsementEditPanelProps {
  policyId: string;
  endorsementId: string;
  policyIsActive?: boolean;
  /** Called after successful save — parent transitions drawer back to detail view */
  onSave: () => void;
  /** Called on cancel — parent transitions drawer back to detail view */
  onCancel: () => void;
}

/**
 * Edit flow for an existing endorsement.
 * Pre-populates the EndorsementConfigForm with the current config values.
 *
 * Rendered inside the SideDrawer when DrawerContent is { type: "endorsement-edit" }.
 */
export const EndorsementEditPanel = ({
  policyId,
  endorsementId,
  policyIsActive = false,
  onSave,
  onCancel,
}: EndorsementEditPanelProps) => {
  const { getById, updateEndorsement } = useEndorsements(policyId, policyIsActive);
  const endorsement = getById(endorsementId);

  if (!endorsement) {
    return (
      <Box sx={{ py: 4, textAlign: "center" }}>
        <Typography variant="body2" color="text.disabled">
          Endorsement not found.
        </Typography>
      </Box>
    );
  }

  const handleSubmit = (data: Record<string, unknown>) => {
    updateEndorsement(endorsementId, data, onSave);
  };

  // Merge common fields + config into flat defaults for the form
  const defaultValues: Record<string, unknown> = {
    is_enabled: endorsement.is_enabled,
    valid_period_start: endorsement.valid_period_start,
    valid_period_end: endorsement.valid_period_end,
    ...(endorsement.config ?? {}),
  };

  return (
    <EndorsementConfigForm
      endorsementType={endorsement.type}
      defaultValues={defaultValues}
      onSubmit={handleSubmit}
      onCancel={onCancel}
      submitLabel="Save Changes"
    />
  );
};
